/**
 * Station manager view (COC-STATION-V1, V4-P1, Scope 2) -- per-operator
 * workload + accountability for the EXISTING manager-tracking audience
 * (owner / gm_agm / regional_head EXACTLY; COC admission = named seam
 * pending tenant question 7). SEE-NOT-DO: counts + labels only; every count
 * drills to its evidence rows (the R-38 pattern) on a surface the role
 * already holds (LES-016): the command center (V2-P1b leadership read).
 *
 * SCOPE + WINDOW HONESTY (R-47/R-49 convention): every number is over the
 * caller's own verified scope; the conversion column names its window and
 * grain in the header (contact-grain, THE owner-funnel derivation, consumed
 * verbatim server-side). Outcomes-recorded is LEAD-OWNER-attributed and the
 * label says so (lead_events carries no actor column -- a named seam).
 *
 * Self-fetching + fault-isolated (a fault here never breaks the host page).
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  fetchCocStationManagerView,
  type CocStationManagerViewData,
} from '@/dashboard/api';
import { SlaBreachStrip } from '@/dashboard/lead-cockpit/CallcenterCommandCenter';
import { formatIstDateTime } from '@/lib/datetime-ist';

export type StationManagerLoadState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; data: CocStationManagerViewData };

export interface StationManagerPanelProps {
  /** TEST-ONLY state injection (static renders cannot run effects). */
  readonly initialState?: StationManagerLoadState;
}

export default function StationManagerPanel({
  initialState,
}: StationManagerPanelProps = {}) {
  const [state, setState] = useState<StationManagerLoadState>(
    initialState ?? { kind: 'loading' },
  );

  useEffect(() => {
    if (initialState !== undefined) return;
    let cancelled = false;
    void fetchCocStationManagerView()
      .then((data) => {
        if (!cancelled) setState({ kind: 'ready', data });
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setState({
            kind: 'error',
            message: e instanceof Error ? e.message : String(e),
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [initialState]);

  if (state.kind === 'loading') {
    return (
      <section className="coc-station-manager" data-testid="station-manager-loading">
        <div className="loading">Loading operator workload...</div>
      </section>
    );
  }
  if (state.kind === 'error') {
    return (
      <section className="coc-station-manager" data-testid="station-manager-error">
        <p className="panel-caveat">
          Operator workload could not load: {state.message}
        </p>
      </section>
    );
  }

  const { data } = state;
  const { view } = data;

  return (
    <section
      className="coc-station-manager"
      data-testid="station-manager"
      aria-label="Operator workload and accountability"
    >
      <header className="coc-station-manager__head">
        <div>
          <span className="coc-station__eyebrow">Recovery desk</span>
          <h2 className="coc-station__title">Operator workload (your scope)</h2>
        </div>
        <span className="coc-station__generated">
          As of {formatIstDateTime(data.generatedAtIso)}
        </span>
      </header>

      <div className="coc-station-manager__strip" data-testid="station-manager-strip">
        <Link
          to="/dashboard/lead-cockpit"
          className="coc-station__tile coc-station__tile--warn"
          data-testid="manager-tile-unclaimed-escalated"
        >
          <span className="coc-station__tile-value">
            {view.pool.unclaimedPastEscalation}
          </span>
          <span className="coc-station__tile-label">
            Unclaimed past escalation ({view.escalationMinutes}m) - of{' '}
            {view.pool.unclaimed} unclaimed
          </span>
        </Link>
        {data.role === 'owner' ? (
          // Role-shaped door (LES-016): the org-intake BACKEND read admits
          // only the configured intake-owner role + owner -- gm_agm /
          // regional_head would land on an access-restricted page, so they
          // get the COUNT without the door (never a click that dies).
          <Link
            to="/dashboard/org-intake"
            className="coc-station__tile"
            data-testid="manager-tile-needs-clinic"
          >
            <span className="coc-station__tile-value">
              {view.needsClinicAssignment}
            </span>
            <span className="coc-station__tile-label">
              Leads needing clinic assignment (org)
            </span>
          </Link>
        ) : (
          <div
            className="coc-station__tile"
            data-testid="manager-tile-needs-clinic-static"
          >
            <span className="coc-station__tile-value">
              {view.needsClinicAssignment}
            </span>
            <span className="coc-station__tile-label">
              Leads needing clinic assignment (org) - worked on the org-intake
              queue
            </span>
          </div>
        )}
      </div>

      {/* SLA-ONE-DERIVATION-AND-STATION-STRIP-V1 (Refs #315): the per-class
          breach strip over the SAME scoped station rows every number on this
          panel aggregates (view.slaBreachCounts, computed server-side from the
          ONE V4-P0 classifier's output -- the same derivation the per-operator
          "Past SLA" column counts distinct leads from). Absent when zero;
          tolerant of an older/partial wire (LES-057: absent -> no strip). */}
      <SlaBreachStrip counts={view.slaBreachCounts} />

      {view.capped ? (
        <p className="panel-caveat" data-testid="station-manager-capped">
          Large scope: numbers are computed over the newest slice, a lower
          bound.
        </p>
      ) : null}

      {view.operators.length === 0 ? (
        <p className="coc-station__empty" data-testid="station-manager-empty">
          No leads are assigned to operators in your scope yet.
        </p>
      ) : (
        <div className="coc-station__table-wrap">
          <table className="table" data-testid="station-manager-table">
            <thead>
              <tr>
                <th scope="col">Operator</th>
                <th scope="col">Assigned open</th>
                <th scope="col">Overdue ({view.escalationMinutes}m)</th>
                <th scope="col">Callbacks due</th>
                <th scope="col">Past SLA</th>
                <th scope="col">Outcomes today (their leads)</th>
                <th scope="col">
                  Converted {view.conversionWindow.from} to{' '}
                  {view.conversionWindow.toExclusive} (UTC, by phone match)
                </th>
              </tr>
            </thead>
            <tbody>
              {view.operators.map((op) => (
                <tr key={op.staffUserId} data-testid={`manager-op-${op.staffUserId}`}>
                  <td>{op.label}</td>
                  <td>{op.assignedOpen}</td>
                  <td>{op.overdueAssigned}</td>
                  <td>{op.callbacksDue}</td>
                  <td>{op.slaBreached}</td>
                  <td>{op.outcomesRecordedToday}</td>
                  <td>{op.convertedInWindow}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="coc-station__footnote">
        Evidence rows:{' '}
        <Link to="/dashboard/lead-cockpit">open the command center</Link> for
        the queue behind these counts.
      </p>
    </section>
  );
}
